import React, { useState, useEffect } from 'react';
import axiosInstance from '../store/axiosInterceptor';
import { useToast } from '../components/Toast/ToastContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Radio, RadioGroup, Button } from '@mui/material';
import { CardSkeleton } from '../components/Skeleton';

interface Coach {
  id?: string;
  coach_id?: string;
  name: string;
  email: string;
  bio: string;
  area_of_expertise?: string;
  status?: string;
}

interface CoachDetail {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone?: string;
  bio?: string;
  area_of_expertise: string;
  approved?: boolean;
  status?: string;
}

interface StudentDetail {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role: string;
  focus_area?: string;
  avatar?: string;
}

interface SuggestedMatch {
  coach: CoachDetail;
  student: StudentDetail;
  reasoning: string;
  score: number;
}

interface Assignment {
  id: string;
  student_id: string;
  coach_id: string;
  student_name: string;
  student_email: string;
  student_avatar?: string;
  coach_name: string;
  coach_email: string;
  coach_area_of_expertise?: string;
  assigned_date: string;
}

// Helper component to display avatar with first letter fallback
const AvatarDisplay: React.FC<{ name: string; imageUrl?: string; size?: string }> = ({ name, imageUrl, size = '48' }) => {
  const [imgError, setImgError] = useState(false);
  const sizeClass = size === '48' ? 'h-12 w-12' : size === '40' ? 'h-10 w-10' : 'h-12 w-12';
  
  if (!imageUrl || imgError) {
    const firstLetter = name.charAt(0).toUpperCase();
    return (
      <div className={`${sizeClass} rounded-full bg-gray-200 flex items-center justify-center text-black font-bold text-sm`}>
        {firstLetter}
      </div>
    );
  }
  
  return (
    <img
      src={imageUrl}
      alt={name}
      className={`${sizeClass} rounded-full object-cover`}
      onError={() => setImgError(true)}
    />
  );
};

const StudentRequestsPage: React.FC = () => {
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [suggestedMatches, setSuggestedMatches] = useState<SuggestedMatch[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'assignments' | 'suggestions'>('assignments');
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedCoachId, setSelectedCoachId] = useState<string | null>(null);
  const [showCoachModal, setShowCoachModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [assignedCoach, setAssignedCoach] = useState<CoachDetail | null>(null);
  const [assignedStudent, setAssignedStudent] = useState<StudentDetail | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    fetchCoaches();
    fetchAISuggestions();
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-switch to suggestions tab if there are pending requests to assign
  useEffect(() => {
    if (!loading && suggestedMatches.length > 0 && assignments.length === 0) {
      setActiveTab('suggestions');
    }
  }, [loading, suggestedMatches, assignments]);

  const fetchCoaches = async () => {
    try {
      const response = await axiosInstance.get(
        `/api/coaches`
      );
      // Show all coaches
      setCoaches(response.data || []);
    } catch (error: any) {
      showToast('Failed to fetch coaches', 'error');
    }
  };

  const fetchAISuggestions = async () => {
    try {
      setLoading(true);
      const suggestionsUrl = process.env.REACT_APP_SUGGESTIONS_URL || '/api/suggestions/matches';
      const isAbsolute = /^https?:\/\//i.test(suggestionsUrl);
      const response = isAbsolute
        ? await axiosInstance.get(suggestionsUrl)
        : await axiosInstance.get(suggestionsUrl);
      console.log('Fetched AI suggestions:', response.data);
      setSuggestedMatches(response.data?.matches || []);
    } catch (error: any) {
      showToast('Failed to fetch matches', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await axiosInstance.get('/api/requests/assignments');
      setAssignments(response.data?.assignments || []);
    } catch (error: any) {
      console.log('Failed to fetch assignments:', error);
      // It's okay if this endpoint doesn't exist yet
    }
  };



  const assignCoachByStudent = async (studentId: string, coachId: string, message?: string) => {
    await axiosInstance.post('/api/requests/assign-by-student', {
      student_id: studentId,
      coach_id: coachId,
      message,
    });
  };



  const handleCoachSelect = async () => {
    if (!selectedCoachId || !selectedStudentId) return;
    
    try {
      await assignCoachByStudent(selectedStudentId, selectedCoachId);
      
      // Find the coach and student details for the success modal
      const selectedCoach = coaches.find(c => (c.id || c.coach_id) === selectedCoachId);
      const matchedStudent = suggestedMatches.find(m => m.student.id === selectedStudentId)?.student;
      
      if (selectedCoach && matchedStudent) {
        setAssignedCoach({
          id: selectedCoachId,
          user_id: selectedCoachId,
          name: selectedCoach.name,
          email: selectedCoach.email,
          area_of_expertise: selectedCoach.area_of_expertise,
        } as CoachDetail);
        
        setAssignedStudent({
          id: selectedStudentId,
          name: matchedStudent.name,
          email: matchedStudent.email,
          role: matchedStudent.role,
          avatar: matchedStudent.avatar,
        } as StudentDetail);
        
        setShowSuccessModal(true);
      }
      
      setShowCoachModal(false);
      setSelectedStudentId(null);
      setSelectedCoachId(null);
      
      // Refresh matches and assignments
      await fetchAISuggestions();
      await fetchAssignments();
    } catch (error: any) {
      showToast('Failed to assign coach', 'error');
    }
  };

  const handleAssignFromSuggestion = async (match: SuggestedMatch) => {
    try {
      await assignCoachByStudent(
        match.student.id,
        match.coach.id,
        `Assigned from AI suggestion: ${match.coach.name}`
      );

      showToast('AI suggestion approved successfully', 'success');
      
      // Refresh matches and assignments
      await fetchAISuggestions();
      await fetchAssignments();
    } catch (error: any) {
      showToast('Failed to approve AI suggestion', 'error');
    }
  };

  const handleAssignDifferentFromOrphan = (studentId: string) => {
    setSelectedStudentId(studentId);
    setSelectedCoachId(null);
    setSearchQuery('');
    setShowCoachModal(true);
  };



  if (loading) {
    return (
      <div className="space-y-5">
        {/* Tabs Skeleton */}
        <div className="flex gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
        {/* Request Cards Skeleton */}
        <div className="space-y-5">
          {[1, 2, 3].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="flex gap-3 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('assignments')}
          className={`px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'assignments'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Active Assignments {assignments.length > 0 && `(${assignments.length})`}
        </button>
        <button
          onClick={() => setActiveTab('suggestions')}
          className={`px-4 py-3 text-sm font-medium transition-colors ${
            activeTab === 'suggestions'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Suggested Matches {suggestedMatches.length > 0 && `(${suggestedMatches.length})`}
        </button>
      </div>

      {/* Active Assignments Tab */}
      {activeTab === 'assignments' && (
        <div className="space-y-4">
          {assignments.length > 0 ? (
            <div className="space-y-4">
              {assignments.map((assignment) => (
                <div key={assignment.id} className="rounded-xl border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <AvatarDisplay name={assignment.student_name} imageUrl={assignment.student_avatar} size="48" />
                      <div>
                        <p className="text-base font-semibold text-gray-900">{assignment.student_name}</p>
                        <p className="text-sm text-gray-500">Student</p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">
                      Assigned: {new Date(assignment.assigned_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>

                  <p className="text-sm text-gray-700 mb-4">
                    <span className="font-medium">Email:</span> {assignment.student_email}
                  </p>

                  <div className="rounded-lg bg-green-50 p-6 border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full bg-green-600"></span>
                      <h3 className="text-xs font-bold uppercase tracking-wide text-green-700">
                        Assigned Coach
                      </h3>
                    </div>

                    <div className="flex items-start gap-4">
                      <AvatarDisplay name={assignment.coach_name} imageUrl={undefined} size="48" />
                      <div>
                        <p className="text-base font-semibold text-gray-900">{assignment.coach_name}</p>
                        <p className="text-sm text-gray-500">{assignment.coach_email}</p>
                        {assignment.coach_area_of_expertise && (
                          <p className="text-sm text-gray-600 mt-2">
                            <span className="font-medium">Area of Expertise:</span> {assignment.coach_area_of_expertise}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-[#F2F4F7] bg-white py-12 text-center">
              <p className="text-sm text-gray-400">No active assignments yet</p>
            </div>
          )}
        </div>
      )}

      {/* Suggested Matches Tab */}
      {activeTab === 'suggestions' && (
        <div className="space-y-4">
          {suggestedMatches.map((match: SuggestedMatch, idx: number) => (
            <div key={`ai-${match.student.id || idx}`} className="rounded-lg border border-[#F2F4F7] bg-white p-6 shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
              {/* Student Info Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <AvatarDisplay name={match.student.name} imageUrl={match.student.avatar} size="48" />
                  <div>
                    <p className="text-base font-semibold text-gray-900">{match.student.name}</p>
                    <p className="text-sm text-gray-500">Student</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">Submitted: {new Date(match.student.avatar || Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
              </div>

              {/* Goals */}
              {match.student.focus_area && (
                <p className="text-sm text-gray-700 mb-4">
                  <span className="font-medium">Goals:</span> {match.student.focus_area}
                </p>
              )}

              {/* AI Suggested Match Box */}
              <div className="mt-4 rounded-lg bg-green-50 p-6 border-2 border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-green-600"></span>
                  <h3 className="text-xs font-bold uppercase tracking-wide text-green-700">
                    AI SUGGESTED MATCH
                  </h3>
                </div>

                {/* Coach Info with Match Score */}
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-4">
                    <AvatarDisplay name={match.coach.name} imageUrl={undefined} size="48" />
                    <div>
                      <p className="text-base font-semibold text-gray-900">{match.coach.name}</p>
                      <p className="text-sm text-gray-500">Coach</p>
                      {match.coach.area_of_expertise && (
                        <p className="text-sm text-gray-600 mt-2">
                          <span className="font-medium">Area of Expertise:</span> {match.coach.area_of_expertise}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Match Score */}
                  <div className="text-right">
                    <p className="text-4xl font-bold text-green-600">{match.score}%</p>
                    <p className="text-xs text-green-600 font-medium">match</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mt-6">
                <button
                  onClick={() => handleAssignFromSuggestion(match)}
                  className="rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Approve AI Suggestion
                </button>
                <button
                  onClick={() => handleAssignDifferentFromOrphan(match.student.id)}
                  className="rounded-lg border border-gray-300 bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Assign Different Coach
                </button>
              </div>
            </div>
          ))}

          {suggestedMatches.length === 0 && (
            <div className="rounded-xl border border-[#F2F4F7] bg-white py-12 text-center">
              <p className="text-sm text-gray-400">No suggested matches found</p>
            </div>
          )}
        </div>
      )}

      {/* Coach Selection Modal */}
      <Dialog
        open={showCoachModal}
        onClose={() => {
          setShowCoachModal(false);
          setSelectedCoachId(null);
          setSelectedStudentId(null);
          setSearchQuery('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '600px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Select a Coach</h3>
            <p className="text-sm text-gray-500 mt-0.5">Choose a coach to assign to this student request</p>
          </div>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {/* Search Input */}
          <div className="mb-4">
            <TextField
              fullWidth
              size="small"
              placeholder="Search coaches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <svg className="h-5 w-5 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </div>
          
          {/* Coach List */}
          <RadioGroup
            value={selectedCoachId || ''}
            onChange={(e) => setSelectedCoachId(e.target.value)}
          >
            <div className="space-y-3 max-h-[350px] overflow-y-auto pr-2">
              {coaches
                .filter(coach => 
                  coach.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  coach.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (coach.area_of_expertise?.toLowerCase().includes(searchQuery.toLowerCase()))
                )
                .map((coach) => {
                  const rawCoachId = coach.id || coach.coach_id;
                  const coachIdStr = rawCoachId ? String(rawCoachId) : '';
                  const isSelected = selectedCoachId === coachIdStr;
                  return (
                    <label
                      key={coachIdStr || coach.email}
                      htmlFor={coachIdStr ? `coach-${coachIdStr}` : undefined}
                      className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-[#F2F4F7] bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {/* Radio Button */}
                      <Radio
                        id={coachIdStr ? `coach-${coachIdStr}` : undefined}
                        value={coachIdStr}
                        disabled={!coachIdStr}
                        sx={{ padding: 0 }}
                      />
                      
                      {/* Avatar */}
                      <AvatarDisplay name={coach.name} imageUrl={undefined} size="40" />
                      
                      {/* Coach Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate">{coach.name}</p>
                        <p className="text-sm text-gray-600 truncate">{coach.email}</p>
                        {coach.area_of_expertise && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{coach.area_of_expertise}</p>
                        )}
                      </div>
                    </label>
                  );
                })}
            </div>
          </RadioGroup>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => {
              setShowCoachModal(false);
              setSelectedCoachId(null);
              setSelectedStudentId(null);
              setSearchQuery('');
            }}
            variant="outlined"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              flex: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCoachSelect}
            disabled={!selectedCoachId}
            variant="contained"
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              flex: 1,
              bgcolor: '#2563eb',
              '&:hover': {
                bgcolor: '#1d4ed8',
              },
            }}
          >
            Assign Coach
          </Button>
        </DialogActions>
      </Dialog>

      {/* Coach Assigned Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 3, px: 3 }}>
          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Coach Assigned</h2>
          
          {/* Avatars */}
          <div className="flex justify-center items-center gap-4 mb-6">
            <div className="text-center">
              <AvatarDisplay 
                name={assignedStudent?.name || ''} 
                imageUrl={assignedStudent?.avatar}
                size="48"
              />
              <p className="text-sm font-medium text-gray-900 mt-2 truncate">
                {assignedStudent?.name}
              </p>
            </div>
            
            {/* Arrow icon */}
            <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 10l-2.293-2.293a1 1 0 010-1.414zm-8 8a1 1 0 011.414 0L7.586 15H4a1 1 0 110-2h4.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            
            <div className="text-center">
              <AvatarDisplay 
                name={assignedCoach?.name || ''} 
                imageUrl={undefined}
                size="48"
              />
              <p className="text-sm font-medium text-gray-900 mt-2 truncate">
                {assignedCoach?.name}
              </p>
            </div>
          </div>
          
          {/* Message */}
          <p className="text-gray-600 mb-6">
            <span className="font-semibold text-gray-900">{assignedCoach?.name}</span>
            {' will be coaching '}
            <span className="font-semibold text-gray-900">{assignedStudent?.name}</span>
          </p>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={() => setShowSuccessModal(false)}
            variant="contained"
            fullWidth
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              bgcolor: '#2563eb',
              '&:hover': {
                bgcolor: '#1d4ed8',
              },
              py: 1.5,
              fontSize: '1rem',
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default StudentRequestsPage;
